import type { BillingStatus, BillingStatusLevel, BillingSubscription } from '../types/billing';

// Maps the raw Stripe subscription status to the org-facing billing level. Used
// only as a fallback if the backend ever omits the derived `billing_status`.
const STATUS_TO_LEVEL: Record<BillingSubscription['status'], BillingStatusLevel> = {
  trialing: 'TRIAL',
  active: 'ACTIVE',
  past_due: 'PAST_DUE',
  canceled: 'CANCELED',
  unpaid: 'PAST_DUE',
  suspended: 'PAST_DUE'
};

/**
 * Resolve the high-level billing state to drive the UI. Prefers the backend's
 * derived `billing_status` (top-level), falling back to the subscription's own
 * field and finally to a status-based derivation so the UI stays correct even if
 * the contract is partially populated.
 */
export const resolveBillingLevel = (data?: BillingStatus | null): BillingStatusLevel => {
  if (!data) {
    return 'NONE';
  }

  if (data.billing_status) {
    return data.billing_status;
  }

  const subscription = data.subscription;
  if (!subscription) {
    return 'NONE';
  }

  return subscription.billing_status ?? STATUS_TO_LEVEL[subscription.status] ?? 'NONE';
};

/**
 * Days remaining in the trial, rounded up. Returns null when there is no trial
 * end or it is already in the past.
 */
export const getTrialDaysRemaining = (trialEnd?: string | null): number | null => {
  if (!trialEnd) {
    return null;
  }

  const end = new Date(trialEnd).getTime();
  if (Number.isNaN(end)) {
    return null;
  }

  const diffMs = end - Date.now();
  if (diffMs <= 0) {
    return null;
  }

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};
